import React from 'react';
import PropTypes from 'prop-types';
import { Popup } from 'semantic-ui-react';
import { ExclamationTriangle } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import {
  TooltipTriggerIcon,
  TooltipContent,
  TooltipSection,
  TooltipSectionTitle,
  TooltipList,
  TooltipText
} from './styled';

const CouplingStatusTooltip = ({
  temperatureMissing,
  vibrationMissing,
  missingAlarms,
  axisToBackendDirection,
  getDirectionDisplayLabel,
  unavailableFeatures
}) => {
  const { t } = useTranslation();
  const hasMissingAlarms = temperatureMissing || vibrationMissing;
  const hasUnavailableFeatures = unavailableFeatures && unavailableFeatures.length > 0;

  // Don't show tooltip if nothing to display
  if (!hasMissingAlarms && !hasUnavailableFeatures) {
    return null;
  }

  const tooltipContent = (
    <TooltipContent>
      {hasMissingAlarms && (
        <TooltipSection>
          <TooltipSectionTitle>{t('sensors.eventDetection.couplingStatus.missingTitle')}</TooltipSectionTitle>
          <TooltipList>
            {temperatureMissing && <li>{t('sensors.eventDetection.couplingStatus.temperatureAlarmThreshold')}</li>}
            {vibrationMissing && missingAlarms && missingAlarms.vibration && 
              Object.entries(axisToBackendDirection).map(([, direction]) => {
                if (missingAlarms.vibration[direction] === true) {
                  const directionLabel = getDirectionDisplayLabel(direction);
                  return (
                    <li key={direction}>
                      {t('sensors.eventDetection.couplingStatus.vibrationAlarmThreshold', { direction: directionLabel })}
                    </li>
                  );
                }
                return null;
              })
            }
          </TooltipList>
          <TooltipText>{t('sensors.eventDetection.couplingStatus.configureThresholdsHint')}</TooltipText>
        </TooltipSection>
      )}

      {hasUnavailableFeatures && (
        <TooltipSection isLast={!hasMissingAlarms}>
          <TooltipSectionTitle>{t('sensors.eventDetection.couplingStatus.partialCouplingTitle')}</TooltipSectionTitle>
          <TooltipText>{t('sensors.eventDetection.couplingStatus.partialCouplingIntro')}</TooltipText>
          <TooltipList>
            {unavailableFeatures.map((detail, index) => (
              <li key={index}>
                <strong>{detail.feature}:</strong> {detail.reason}
              </li>
            ))}
          </TooltipList>
          <TooltipText>
            {t('sensors.eventDetection.couplingStatus.partialCouplingOutro')}
          </TooltipText>
        </TooltipSection>
      )}
    </TooltipContent>
  );

  return (
    <Popup
      trigger={(
        <TooltipTriggerIcon isWarning={hasMissingAlarms}>
          <ExclamationTriangle aria-label={t('common.aria.couplingStatus', { defaultValue: 'coupling status' })} />
        </TooltipTriggerIcon>
      )}
      content={tooltipContent}
      position="bottom left"
      wide="very"
      hoverable
      on="hover"
    />
  );
};

CouplingStatusTooltip.propTypes = {
  temperatureMissing: PropTypes.bool,
  vibrationMissing: PropTypes.bool,
  missingAlarms: PropTypes.object,
  axisToBackendDirection: PropTypes.object,
  getDirectionDisplayLabel: PropTypes.func,
  unavailableFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      reason: PropTypes.string.isRequired
    })
  )
};

CouplingStatusTooltip.defaultProps = {
  temperatureMissing: false,
  vibrationMissing: false,
  missingAlarms: null,
  axisToBackendDirection: {},
  getDirectionDisplayLabel: () => '',
  unavailableFeatures: []
};

export default React.memo(CouplingStatusTooltip);
